import type { UiLocale } from '../coachCatalog.js';

/**
 * Textos de la charla asistida (encargo 99, diseño cerrado de #468): el selector
 * «Solo / Asistida» de la pantalla previa, la hoja de instrucciones, la tarjeta
 * de la respuesta modelo y las notitas de las burbujas. Español primero (voseo);
 * el par en inglés queda listo para cuando la UI tenga idioma.
 */
export interface AssistedCopy {
  modeEyebrow: string;
  modeAria: string;
  solo: string;
  assisted: string;
  /** La línea de estado del control, según el modo elegido. */
  modeLine: { solo: string; assisted: string };
  /** Por qué está así: por el nivel (nunca eligió) o porque lo eligió (vale para todos). */
  modeWhy: {
    levelAssisted: (cefr: string) => string;
    levelSolo: string;
    chosenSolo: string;
    chosenAssisted: string;
    /** No cargó el nivel o las prefs: «Solo» (como antes del selector), o la elección. */
    unknown: string;
  };
  /** No se pudo guardar la elección. */
  modeSaveFailed: string;
  /** No cargaron las prefs: decide el nivel, la elección guardada no se vio. */
  modeLoadFailed: string;
  /** La hoja corta al empezar en modo asistido. */
  intro: {
    title: string;
    sub: (scene: string) => string;
    steps: readonly string[];
    note: string;
    start: string;
    solo: string;
  };
  card: {
    tag: (n: number) => string;
    angle: string;
    loadingTag: string;
    sayIt: string;
    listening: string;
    /** Grabando con un toque (fijada): se manda tocando otra vez. */
    listeningLocked: string;
    judging: string;
    /** Debajo del micrófono de la hoja (como en el repaso). */
    micHold: string;
    micHolding: string;
    micLocked: string;
    /** El sello al decirla bien. */
    said: string;
    listen: string;
    listening2: string;
    follow: string;
    repeat: string;
    goSolo: string;
    /** «Descartar esta frase» (Emmanuel, 19/10): corto en la fila de arriba; el aria dice todo. */
    discard: string;
    discardAria: string;
    retry: string;
    error: string;
    heard: (t: string) => string;
    titleSilence: string;
    textSilence: string;
    titleRepeat: (swap: boolean) => string;
    /** Dijo otra frase (se oyó menos de la mitad): ni «Casi» ni «te faltó». */
    titleOther: string;
    textOther: string;
    swap: (said: string, model: string) => string;
    /** Lo que faltó, por tramos («a», «b» y «c»). */
    missing: (chunks: readonly string[]) => string;
    extra: (w: string) => string;
  };
  offer: {
    tag: string;
    angle: string;
    title: string;
    text: string;
    /** El extra no entró: practicó sólo los principales. */
    textMain: string;
    note: string;
    again: string;
    /** Junto a cada objetivo de la oferta: lo practicó con ayuda. */
    withHelp: string;
    solo: string;
    /** Terminar la escena acá y ver el cierre (el «Terminar» de las escenas queda tapado). */
    finish: string;
  };
  soloNow: {
    tag: string;
    title: string;
    text: string;
  };
  bubble: {
    said: string;
    followed: string;
    warnMissing: (w: string) => string;
    warnExtra: (w: string) => string;
    restarted: string;
    covered: string;
  };
  /** El repaso de las correcciones con el coach (cierre y Repaso del día). */
  review: {
    tagClose: string;
    tagDay: string;
    /** «Practicar estas N» al final de una partida (plan 47). */
    tagGame: string;
    /** «Repasar estas frases» en la ficha de un foco del Radar (Emmanuel, 19/10). */
    tagRadar: string;
    /** «Decilas ahora»: las palabras que guardó en la Bodega de la nave (Emmanuel, 20/10). */
    tagWords: string;
    /** La tarjeta es de un foco activo del Radar (Emmanuel, 19/10). */
    radar: string;
    radarOf: (label: string) => string;
    /** Una palabra guardada en el Repaso del día (Emmanuel, 20/10): sin mirar, sin nombrarla. */
    savedWord: string;
    savedWordOf: (word: string) => string;
    /**
     * La explicación del juego que escribe el coach UNA vez por cuenta
     * (`reviewGuideSeen`): en la primera tarjeta y al pasar a sin mirar.
     */
    guideHelp: string;
    guideSolo: string;
    /** El botón de arriba y su hoja: cómo se juega y las reglas. */
    howTo: string;
    howToSteps: readonly string[];
    howToRulesTitle: string;
    howToRules: readonly string[];
    howToClose: string;
    /** El rótulo de la tarjeta con la frase a la vista. */
    tagHelp: string;
    tagSolo: string;
    /** «Intento 2/3» en la vuelta sin mirar. */
    attempt: (n: number, of: number) => string;
    askHelp: string;
    askSolo: string;
    /** Grabando: «Te escucho…» + soltá / tocá cuando termines. */
    hearing: string;
    releaseWhenDone: string;
    tapWhenDone: string;
    checking: string;
    silence: string;
    seePhrase: string;
    /** Con la frase a la vista: pasar a la vuelta sin mirar sin decirla. */
    skip: string;
    next: string;
    close: string;
    hold: string;
    holding: string;
    /** Después de un toque: la toma sigue abierta hasta el próximo toque. */
    holdingLocked: string;
    /** Sin mirar: el rótulo de la pista en español. */
    cueLabel: string;
    /** Los sellos de la tarjeta. */
    stampHelp: string;
    stampMemory: string;
    stampRetry: string;
    stampLearned: string;
    stampAgain: string;
    countsAgain: string;
    /** De memoria al 1.º intento, con cuándo vuelve («vuelve en 7 días»). */
    countsKnown: (when: string | null) => string;
    /** Bien al 2.º o 3.º intento: cuenta como bien, sin subir de escalón. */
    countsRetry: (when: string | null) => string;
    /** 3 vueltas de memoria: aprendida. */
    countsLearned: string;
    /** Mal sin mirar, con intentos todavía. */
    triesLeft: (n: number) => string;
    /** Sin mirar, con intentos: volvió el error de la charla (sin decir cuál). */
    errorBack: string;
    /** Sin mirar, con intentos: la regla del error, sin la respuesta. */
    hint: (rule: string) => string;
    sameAsBefore: string;
    /**
     * Mal los 3 sin mirar (vuelve mañana): ya no hay micrófono, sólo «Siguiente»,
     * así que lo que pasó se dice sin pedir que la diga de nuevo (card #571).
     */
    decidedSame: string;
    decidedOther: string;
    missingCorrection: (chunks: readonly string[]) => string;
    cameBack: (w: string) => string;
    checkError: string;
    /** No salió: el micrófono ya sirve para otra toma (sin botón, como en la tarjeta de la charla). */
    sayAgain: string;

    /** Bien, de otra forma correcta que no usa la guardada (Emmanuel, 15/10). */
    ideaOk: string;
    /** Rótulo de lo que dijo, cuando pasó de otra forma. */
    youSaid: string;
    /** «También podrías decir «frase», <por qué>.» */
    alsoSay: (phrase: string, tip: string) => string;
    /** «También podrías decir «frase».» (el juez no dio un porqué). */
    alsoSayPlain: (phrase: string) => string;
    /** Lo practicado bien con un error nuevo en el resto: «¡Bien el "I'd like"!». */
    slipGood: (point: string) => string;
    /** «Ojo: se dice "an improvement" o "an upgrade".» */
    slipNote: (note: string) => string;
    micError: string;
    doneTitle: string;
    ringLabel: string;
    /** Cuántas salieron sin mirar (al 1.º intento o después) de cuántas. */
    doneSub: (solo: number, total: number) => string;
    tallyKnown: string;
    tallyKnownWhen: string;
    tallyPracticed: string;
    tallyPracticedWhen: string;
    /** Bien sin mirar al 2.º o 3.º intento: aparte, no «de memoria» (card #580). */
    tallyRetry: string;
    tallyRetryWhen: string;
    doneLearned: (n: number) => string;
    /** El recuadro con la fecha, si todas las de ese grupo vuelven el mismo día: «vuelven el sáb 24/10». */
    tallyOn: (day: string) => string;
    /** En la lista del final, una aprendida (ya no vuelve). */
    listLearned: string;
    /**
     * Abajo del final: los días seguidos (los del perfil) y cuántas te tocan mañana
     * (card #604). `null` = no llegó ese dato; con los dos en `null`, nada.
     */
    ahead: (streakDays: number | null, tomorrow: number | null) => string | null;
    saveFailed: string;
    back: string;
  };
}

/** «a», «b» y «c» (las partes que faltaron no se pegan en una sola cita). */
const quoted = (chunks: readonly string[], and: string): string => {
  const q = chunks.map((c) => `«${c}»`);
  return q.length < 2 ? (q[0] ?? '') : `${q.slice(0, -1).join(', ')} ${and} ${q.at(-1)!}`;
};

export const ASSISTED_COPY: Record<UiLocale, AssistedCopy> = {
  es: {
    modeEyebrow: 'CÓMO VAS A PRACTICAR',
    modeAria: 'Solo o asistida',
    solo: 'Solo',
    assisted: 'Asistida',
    modeLine: {
      solo: 'Solo · respondés con tus palabras',
      assisted: 'Asistida · decís una respuesta modelo',
    },
    modeWhy: {
      levelAssisted: (cefr) =>
        `Por tu nivel (${cefr}) te sugerimos empezar con ayuda: el coach te pregunta y te mostramos una respuesta para decir tal cual.`,
      levelSolo: 'Si te trabás, elegí «Asistida»: te mostramos una respuesta para decir tal cual.',
      chosenSolo:
        'Respondés con tus palabras en todos los escenarios. Si te trabás, la bombita te da ideas; «Asistida» está a un toque.',
      chosenAssisted:
        'Practicás con ayuda en todos los escenarios: el coach te pregunta y te mostramos una respuesta para decir tal cual. Lo cambiás acá cuando quieras.',
      unknown:
        'No pudimos cargar tu nivel. Si te trabás, elegí «Asistida»: te mostramos una respuesta para decir tal cual.',
    },
    modeSaveFailed:
      'No pudimos guardar tu elección: vale para esta escena. Probá de nuevo más tarde.',
    modeLoadFailed:
      'No pudimos cargar cómo practicás: te mostramos el modo de tu nivel. Podés cambiarlo acá.',
    intro: {
      title: 'Charla asistida',
      sub: (scene) => `Practicás «${scene}» con ayuda:`,
      steps: [
        'Escuchá lo que te dice el coach.',
        'Abajo vas a ver una respuesta en inglés con su traducción. Si querés, tocá «Escuchar».',
        'Mantené apretado el micrófono y decila tal cual.',
        'Si no te entendemos, tocá «Decila una vez más» o «Seguir». No es un examen.',
      ],
      // Emmanuel (20/10): que quede claro que la misión se cumple después, solo.
      note: 'Este modo no suma palabras a tu vocabulario ni cambia tu nivel, y lo que digas con ayuda no cumple la misión: al terminar te pedimos hacer la escena solo, y así se cumple. Es para dar tus primeros pasos con el inglés, escuchando y repitiendo.',
      start: 'Empezar la escena',
      solo: 'Mejor solo',
    },
    card: {
      tag: (n) => `DECILA TAL CUAL · ${n}`,
      angle: 'Respuesta modelo',
      loadingTag: 'ARMANDO TU RESPUESTA…',
      sayIt: 'Decila tal cual.',
      listening: 'Te escucho… Soltá cuando termines.',
      listeningLocked: 'Te escucho… Cuando termines se manda solo, o tocá el micrófono.',
      judging: 'Escuchando lo que dijiste…',
      micHold: 'Mantené o tocá para hablar',
      micHolding: 'Soltá para mandar',
      micLocked: 'Se manda solo al terminar, o tocá',
      said: '¡Bien dicha!',
      listen: 'Escuchar',
      listening2: 'Reproduciendo…',
      follow: 'Seguir',
      repeat: 'Decila una vez más',
      goSolo: 'Hacelo solo',
      discard: 'Descartar',
      discardAria: 'Descartar esta frase',
      retry: 'Reintentar',
      error: 'No pudimos armar la respuesta modelo.',
      heard: (t) => `Escuché: «${t}»`,
      titleSilence: 'No te escuché',
      textSilence: 'Tocá el micrófono (o mantenelo apretado) y decila.',
      titleRepeat: (swap) => (swap ? 'No te entendí bien' : 'Casi'),
      titleOther: 'Esa es otra frase',
      textOther: 'Decila tal cual está escrita.',
      swap: (said, model) => `«${said}» en lugar de «${model}».`,
      missing: (c) => `Te faltó ${quoted(c, 'y')}.`,
      extra: (w) => `Sobró «${w}».`,
    },
    offer: {
      tag: 'PRÁCTICA ASISTIDA',
      angle: 'Listo',
      title: '¿Probás la escena solo?',
      text: 'Practicaste todos los objetivos con ayuda. Ahora sin respuesta modelo: si te trabás, la bombita te da ideas.',
      textMain:
        'Practicaste los objetivos principales con ayuda (el extra no entró en esta charla). Ahora sin respuesta modelo: si te trabás, la bombita te da ideas.',
      note: 'Lo dicho con ayuda no suma vocabulario ni cambia tu nivel, y no cuenta para la misión.',
      again: 'Seguir con ayuda',
      withHelp: 'con ayuda',
      solo: 'Probar solo',
      finish: 'Terminar y ver mi cierre',
    },
    soloNow: {
      tag: 'AHORA SOLO',
      title: 'Respondé con tus palabras.',
      text: 'Desde acá cuenta para la misión. Si te trabás, la bombita sigue ahí.',
    },
    bubble: {
      said: 'Con ayuda · la dijiste',
      followed: 'Con ayuda · seguiste sin decirla',
      warnMissing: (w) => `te faltó «${w}»`,
      warnExtra: (w) => `sobró «${w}»`,
      restarted: 'te corregiste',
      covered: 'Tapada mientras lo hacés solo',
    },
    review: {
      tagClose: 'REPASO DEL CIERRE',
      tagDay: 'REPASO DEL DÍA',
      tagGame: 'REPASO DE LA PARTIDA',
      tagRadar: 'REPASO DEL RADAR',
      tagWords: 'TUS PALABRAS',
      radar: 'Es de tu radar',
      radarOf: (label) => `Tu radar: ${label}`,
      savedWord: 'Una palabra que guardaste',
      savedWordOf: (word) => `Una palabra que guardaste: ${word}`,
      guideHelp:
        'Acá repasás frases que te corregí en las charlas. Decila tal cual: mantené apretado el micrófono (o tocalo) y hablá.',
      guideSolo:
        'Ahora sin mirar: te digo en español qué decir y vos la decís en inglés. Tenés 3 intentos.',
      howTo: '¿Cómo se juega?',
      howToSteps: [
        'Primero ves una frase que te corregimos en una charla. Decila tal cual: mantené apretado el micrófono (o tocalo) y hablá. Si querés oír cómo suena, tocá «Escuchar».',
        'Después, sin mirar: la frase se tapa, te decimos en español qué decir y la decís en inglés. Las palabras se van llenando mientras hablás.',
        'Las frases que ya repasaste vuelven directo sin mirar.',
      ],
      howToRulesTitle: 'Las reglas',
      howToRules: [
        'Sin mirar tenés 3 intentos. Un silencio no gasta intento.',
        'Bien al primer intento: «¡De memoria!», vuelve en unos días.',
        'Bien al segundo o al tercero: «¡La sacaste!», cuenta como bien.',
        'Mal los 3, o si tocás «Ver la frase»: vuelve mañana.',
        'Después de tres vueltas de memoria: «¡Aprendida!», ya no vuelve.',
      ],
      howToClose: 'Entendido',
      tagHelp: 'DECILA',
      tagSolo: 'SIN MIRAR',
      attempt: (n, of) => `INTENTO ${n}/${of}`,
      askHelp: 'Decila tal cual.',
      askSolo: 'Decila sin mirar.',
      hearing: 'Te escucho…',
      releaseWhenDone: 'soltá cuando termines.',
      tapWhenDone: 'se manda sola cuando terminás (o tocá).',
      checking: 'Escuchando lo que dijiste…',
      silence: 'Tocá el micrófono y decila.',
      seePhrase: 'Ver la frase',
      skip: 'Seguir sin decirla',
      next: 'Siguiente',
      close: 'Cerrar el repaso',
      hold: 'Mantené o tocá para hablar',
      holding: 'Soltá para mandar',
      holdingLocked: 'Se manda solo al terminar, o tocá',
      cueLabel: 'Decila en inglés:',
      stampHelp: '¡Bien dicha!',
      stampMemory: '¡De memoria!',
      stampRetry: '¡La sacaste!',
      stampLearned: '¡Aprendida!',
      stampAgain: 'Vuelve mañana',
      countsAgain: 'Esta la practicás de nuevo mañana.',
      countsKnown: (when) => (when ? `De memoria: ${when}.` : 'De memoria.'),
      countsRetry: (when) =>
        when ? `¡La sacaste! Cuenta como bien: ${when}.` : '¡La sacaste! Cuenta como bien.',
      countsLearned: '¡Aprendida! Ya no vuelve.',
      triesLeft: (n) => (n === 1 ? 'Último intento.' : `Te quedan ${n} intentos.`),
      errorBack: 'Volvió el error de la charla.',
      hint: (rule) => `Pista: ${rule}`,
      sameAsBefore: 'La dijiste igual que en la charla. Mirá lo que cambia y probá otra vez.',
      decidedSame: 'La dijiste igual que en la charla: mirá lo que cambia.',
      decidedOther: 'Era otra frase: mirala bien.',
      missingCorrection: (c) => `Te faltó ${quoted(c, 'y')}, y justo eso es lo que se corrigió.`,
      cameBack: (w) => `Volvió «${w}», y justo eso es lo que se corrigió.`,
      checkError: 'No pudimos escucharte.',
      sayAgain: 'Decila una vez más.',
      ideaOk: '¡Muy bien! Transmitiste correctamente la idea.',
      youSaid: 'Dijiste:',
      alsoSay: (phrase, tip) => `También podrías decir «${phrase}», ${tip}.`,
      alsoSayPlain: (phrase) => `También podrías decir «${phrase}».`,
      slipGood: (point) => `¡Bien el "${point}"!`,
      slipNote: (note) => `Ojo: ${note}.`,
      micError: 'No pudimos abrir el micrófono.',
      doneTitle: '¡Repaso completo!',
      ringLabel: 'de memoria',
      doneSub: (solo, total) =>
        solo === 0
          ? total === 1
            ? 'Mañana la practicás de nuevo'
            : 'Mañana las practicás de nuevo'
          : solo === total
            ? total === 1
              ? 'Te salió sin mirar'
              : `Las ${total} te salieron sin mirar`
            : `${solo} de ${total} te ${solo === 1 ? 'salió' : 'salieron'} sin mirar`,
      tallyKnown: 'De memoria',
      tallyKnownWhen: 'vuelven en unos días',
      tallyPracticed: 'Practicadas',
      tallyPracticedWhen: 'vuelven mañana',
      doneLearned: (n) =>
        n === 1 ? '1 aprendida: ya no vuelve' : `${n} aprendidas: ya no vuelven`,
      tallyRetry: 'Al 2.º o 3.º intento',
      tallyRetryWhen: 'vuelven en 3 días',
      tallyOn: (day) =>
        day === 'mañana' || day === 'hoy' ? `vuelven ${day}` : `vuelven el ${day}`,
      listLearned: 'aprendida',
      ahead: (days, tomorrow) => {
        const parts = [
          days !== null && days > 0
            ? `🔥 ${days === 1 ? '1 día seguido' : `${days} días seguidos`}`
            : null,
          tomorrow !== null && tomorrow > 0
            ? `mañana te ${tomorrow === 1 ? 'toca 1' : `tocan ${tomorrow}`}`
            : null,
        ].filter((p): p is string => p !== null);
        return parts.length > 0 ? parts.join(' · ') : null;
      },
      saveFailed: 'No pudimos guardar algunos resultados: van a volver a aparecer.',
      back: 'Volver al cierre',
    },
  },
  en: {
    modeEyebrow: 'HOW YOU WILL PRACTICE',
    modeAria: 'On your own or assisted',
    solo: 'On your own',
    assisted: 'Assisted',
    modeLine: {
      solo: 'On your own · answer in your own words',
      assisted: 'Assisted · say a model answer',
    },
    modeWhy: {
      levelAssisted: (cefr) =>
        `At your level (${cefr}) we suggest starting with help: the coach asks and we show you an answer to say as is.`,
      levelSolo: 'If you get stuck, pick «Assisted»: we show you an answer to say as is.',
      chosenSolo:
        'You answer in your own words in every scenario. If you get stuck, the bulb gives you ideas; «Assisted» is one tap away.',
      chosenAssisted:
        'You practice with help in every scenario: the coach asks and we show you an answer to say as is. Change it here anytime.',
      unknown:
        'We could not load your level. If you get stuck, pick «Assisted»: we show you an answer to say as is.',
    },
    modeSaveFailed: 'We could not save your choice: it applies to this scene. Try again later.',
    modeLoadFailed:
      'We could not load how you practice: this is the mode for your level. You can change it here.',
    intro: {
      title: 'Assisted conversation',
      sub: (scene) => `You practice «${scene}» with help:`,
      steps: [
        'Listen to what the coach says.',
        'Below you will see an answer in English with its translation. Tap «Listen» if you like.',
        'Hold the microphone and say it as is.',
        'If we do not understand you, tap «Say it once more» or «Continue». It is not an exam.',
      ],
      note: 'This mode does not add words to your vocabulary or change your level, and what you say with help does not complete the mission: when you finish, we ask you to do the scene on your own, and that completes it. It is for your first steps: listening and repeating.',
      start: 'Start the scene',
      solo: 'On my own',
    },
    card: {
      tag: (n) => `SAY IT AS IS · ${n}`,
      angle: 'Model answer',
      loadingTag: 'PREPARING YOUR ANSWER…',
      sayIt: 'Say it as it is.',
      listening: 'Listening… Let go when you finish.',
      listeningLocked: 'Listening… It sends when you stop, or tap the microphone.',
      judging: 'Listening to what you said…',
      micHold: 'Hold or tap to talk',
      micHolding: 'Let go to send',
      micLocked: 'Sends when you stop, or tap',
      said: 'Well said!',
      listen: 'Listen',
      listening2: 'Playing…',
      follow: 'Continue',
      repeat: 'Say it once more',
      goSolo: 'On my own',
      discard: 'Discard',
      discardAria: 'Discard this phrase',
      retry: 'Retry',
      error: 'We could not prepare the model answer.',
      heard: (t) => `I heard: «${t}»`,
      titleSilence: 'I did not hear you',
      textSilence: 'Tap the microphone (or hold it) and say it.',
      titleRepeat: (swap) => (swap ? 'I did not quite get it' : 'Almost'),
      titleOther: 'That is a different phrase',
      textOther: 'Say it just as it is written.',
      swap: (said, model) => `«${said}» instead of «${model}».`,
      missing: (c) => `${quoted(c, 'and')} ${c.length > 1 ? 'were' : 'was'} missing.`,
      extra: (w) => `Extra «${w}».`,
    },
    offer: {
      tag: 'ASSISTED PRACTICE',
      angle: 'Done',
      title: 'Try the scene on your own?',
      text: 'You practiced every goal with help. Now without a model answer: if you get stuck, the bulb gives you ideas.',
      textMain:
        'You practiced the main goals with help (the extra one did not fit in this conversation). Now without a model answer: if you get stuck, the bulb gives you ideas.',
      note: 'What you say with help does not add vocabulary, change your level or count for the mission.',
      again: 'Keep the help',
      withHelp: 'with help',
      solo: 'Try on my own',
      finish: 'Finish and see my summary',
    },
    soloNow: {
      tag: 'ON YOUR OWN',
      title: 'Answer in your own words.',
      text: 'From here it counts for the mission. If you get stuck, the bulb is still there.',
    },
    bubble: {
      said: 'With help · you said it',
      followed: 'With help · continued without saying it',
      warnMissing: (w) => `«${w}» was missing`,
      warnExtra: (w) => `extra «${w}»`,
      restarted: 'you corrected yourself',
      covered: 'Hidden while you do it on your own',
    },
    review: {
      tagClose: 'SESSION REVIEW',
      tagDay: 'TODAY’S REVIEW',
      tagGame: 'GAME REVIEW',
      tagRadar: 'RADAR REVIEW',
      tagWords: 'YOUR WORDS',
      radar: 'From your radar',
      radarOf: (label) => `Your radar: ${label}`,
      savedWord: 'A word you saved',
      savedWordOf: (word) => `A word you saved: ${word}`,
      guideHelp:
        'Here you review phrases I corrected in our talks. Say it as it is: hold the mic (or tap it) and speak.',
      guideSolo:
        'Now without looking: I tell you in Spanish what to say and you say it in English. You have 3 tries.',
      howTo: 'How does it work?',
      howToSteps: [
        'First you see a phrase we corrected in a talk. Say it as it is: hold the mic (or tap it) and speak. To hear how it sounds, tap «Listen».',
        'Then, without looking: the phrase is covered, we tell you in Spanish what to say and you say it in English. The words fill in as you speak.',
        'Phrases you already reviewed come back straight without looking.',
      ],
      howToRulesTitle: 'The rules',
      howToRules: [
        'Without looking you have 3 tries. Silence does not use a try.',
        'Right on the first try: «From memory!», it comes back in a few days.',
        'Right on the second or third: «You got it!», it counts as right.',
        'Wrong all 3, or if you tap «See the phrase»: it comes back tomorrow.',
        'After three rounds from memory: «Learned!», it does not come back.',
      ],
      howToClose: 'Got it',
      tagHelp: 'SAY IT',
      tagSolo: 'WITHOUT LOOKING',
      attempt: (n, of) => `TRY ${n}/${of}`,
      askHelp: 'Say it as it is.',
      askSolo: 'Say it without looking.',
      hearing: 'Listening…',
      releaseWhenDone: 'let go when you finish.',
      tapWhenDone: 'it sends itself when you finish (or tap).',
      checking: 'Listening to what you said…',
      silence: 'Tap the microphone and say it.',
      seePhrase: 'See the phrase',
      skip: 'Continue without saying it',
      next: 'Next',
      close: 'Close the review',
      hold: 'Hold or tap to talk',
      holding: 'Let go to send',
      holdingLocked: 'Sends when you stop, or tap',
      cueLabel: 'Say it in English:',
      stampHelp: 'Well said!',
      stampMemory: 'From memory!',
      stampRetry: 'You got it!',
      stampLearned: 'Learned!',
      stampAgain: 'Back tomorrow',
      countsAgain: 'You will practice this one again tomorrow.',
      countsKnown: (when) => (when ? `From memory: ${when}.` : 'From memory.'),
      countsRetry: (when) => (when ? `You got it! It counts: ${when}.` : 'You got it! It counts.'),
      countsLearned: 'Learned! It will not come back.',
      triesLeft: (n) => (n === 1 ? 'Last try.' : `${n} tries left.`),
      errorBack: 'The mistake from the conversation came back.',
      hint: (rule) => `Hint: ${rule}`,
      sameAsBefore: 'You said it like in the conversation. Look at what changes and try again.',
      decidedSame: 'You said it like in the conversation: look at what changes.',
      decidedOther: 'That was another sentence: take a good look at it.',
      missingCorrection: (c) =>
        `${quoted(c, 'and')} ${c.length > 1 ? 'were' : 'was'} missing, and that is exactly what was corrected.`,
      cameBack: (w) => `«${w}» came back, and that is exactly what was corrected.`,
      checkError: 'We could not hear you.',
      sayAgain: 'Say it once more.',
      ideaOk: 'Well done! You got the idea across correctly.',
      youSaid: 'You said:',
      alsoSay: (phrase, tip) => `You could also say «${phrase}», ${tip}.`,
      alsoSayPlain: (phrase) => `You could also say «${phrase}».`,
      slipGood: (point) => `Good "${point}"!`,
      slipNote: (note) => `Careful: ${note}.`,
      micError: 'We could not open the microphone.',
      doneTitle: 'Review complete!',
      ringLabel: 'from memory',
      doneSub: (solo, total) =>
        solo === 0
          ? total === 1
            ? 'You practice it again tomorrow'
            : 'You practice them again tomorrow'
          : solo === total
            ? total === 1
              ? 'You said it without looking'
              : `You said all ${total} without looking`
            : `You said ${solo} of ${total} without looking`,
      tallyKnown: 'From memory',
      tallyKnownWhen: 'back in a few days',
      tallyPracticed: 'Practiced',
      tallyPracticedWhen: 'back tomorrow',
      doneLearned: (n) =>
        n === 1 ? '1 learned: it will not come back' : `${n} learned: they will not come back`,
      tallyRetry: 'On the 2nd or 3rd try',
      tallyRetryWhen: 'back in 3 days',
      tallyOn: (day) =>
        day === 'mañana' || day === 'hoy'
          ? `back ${day === 'hoy' ? 'today' : 'tomorrow'}`
          : `back on ${day}`,
      listLearned: 'learned',
      ahead: (days, tomorrow) => {
        const parts = [
          days !== null && days > 0
            ? `🔥 ${days === 1 ? '1 day in a row' : `${days} days in a row`}`
            : null,
          tomorrow !== null && tomorrow > 0 ? `${tomorrow} for tomorrow` : null,
        ].filter((p): p is string => p !== null);
        return parts.length > 0 ? parts.join(' · ') : null;
      },
      saveFailed: 'We could not save some results: they will show up again.',
      back: 'Back to the summary',
    },
  },
};

/**
 * Los textos del repaso para las palabras guardadas («Decilas ahora», la Bodega de «Armá tu nave»;
 * Emmanuel, 20/10). El repaso es el mismo, pero estas no se programan: no «vuelven mañana» ni «en unos
 * días»; si no sale, la palabra queda marcada «Me cuesta» en la Biblioteca. Nada que prometa otra cosa.
 */
const WORDS_REVIEW_ES: Partial<AssistedCopy['review']> = {
  guideHelp:
    'Acá decís las palabras que guardaste, cada una en una frase. Decila tal cual: mantené apretado el micrófono (o tocalo) y hablá.',
  howToSteps: [
    'Primero ves la frase de una palabra que guardaste. Decila tal cual: mantené apretado el micrófono (o tocalo) y hablá. Si querés oír cómo suena, tocá «Escuchar».',
    'Después, sin mirar: la frase se tapa, te decimos en español qué decir y la decís en inglés. Las palabras se van llenando mientras hablás.',
  ],
  howToRules: [
    'Sin mirar tenés 3 intentos. Un silencio no gasta intento.',
    'Bien al primer intento: «¡De memoria!».',
    'Bien al segundo o al tercero: «¡La sacaste!», cuenta como bien.',
    'Mal los 3, o si tocás «Ver la frase»: la palabra queda marcada «Me cuesta» en tu Biblioteca.',
  ],
  stampAgain: 'Para practicar',
  countsAgain: 'Queda marcada «Me cuesta» en tu Biblioteca.',
  countsKnown: () => 'De memoria.',
  countsRetry: () => '¡La sacaste! Cuenta como bien.',
  tallyKnownWhen: 'las sabés',
  tallyPracticedWhen: 'marcadas «Me cuesta»',
  tallyRetryWhen: 'con un poco de esfuerzo',
};

/** Los textos del repaso según de dónde viene (las palabras guardadas tienen los suyos). */
export function reviewCopyFor(source: string): AssistedCopy {
  const base = ASSISTED_COPY.es;
  if (source !== 'words') return base;
  return { ...base, review: { ...base.review, ...WORDS_REVIEW_ES } };
}
